import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Loader2, CheckCircle, Circle, ChevronDown, ChevronUp,
  Home, User, Wrench, TrendingUp, Camera, Shield, MapPin, FileText,
  Plus, Trash2, Save, Pencil, RotateCcw
} from "lucide-react";
import { fmtUSD } from "../utils/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTIONS = [
  { key: "basics", label: "Basics", icon: FileText, description: "Contact & loan info" },
  { key: "property", label: "Property Details", icon: Home, description: "Beds, baths, sqft, year built" },
  { key: "borrower_profile", label: "Borrower Profile", icon: User, description: "Entity, guarantor, experience" },
  { key: "rehab_plan", label: "Rehab Plan", icon: Wrench, description: "Itemized renovation budget" },
  { key: "exit_strategy", label: "Exit Strategy", icon: TrendingUp, description: "Sale/refinance plan" },
  { key: "comps", label: "Comparable Sales", icon: MapPin, description: "Nearby recent sales" },
  { key: "photos", label: "Property Photos", icon: Camera, description: "Exterior & interior photos" },
  { key: "risk_analysis", label: "Risk Analysis", icon: Shield, description: "AI-generated strengths & risks" },
];

const STATUS_ICONS = {
  complete: <CheckCircle className="h-4 w-4 text-emerald-500" />,
  partial: <div className="h-4 w-4 rounded-full border-2 border-amber-400 bg-amber-100" />,
  empty: <Circle className="h-4 w-4 text-[#ccc]" />,
};

export default function DealPacketBuilder({ dealId, isInternal = false, userRole = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPacketData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/deals/${dealId}/packet-data`, { headers });
      setData(res.data);
      const merged = { ...res.data.base_data, ...res.data.packet_data };
      setForm(merged);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [dealId]);

  useEffect(() => { fetchPacketData(); }, [fetchPacketData]);

  const saveSection = async (sectionFields) => {
    setSaving(true);
    try {
      const payload = {};
      for (const f of sectionFields) {
        if (form[f] !== undefined && form[f] !== null && form[f] !== "") {
          payload[f] = form[f];
        }
      }
      const res = await axios.put(`${API}/deals/${dealId}/packet-data`, payload, { headers });
      setData(prev => prev ? { ...prev, completeness: res.data.completeness, section_status: res.data.section_status } : prev);
      setEditingSection(null);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;
  if (!data) return <div className="text-center py-10 text-sm text-[#999]">Unable to load deal packet data.</div>;

  const completeness = data.completeness || 0;

  return (
    <div className="space-y-4" data-testid="deal-packet-builder">
      {/* Completeness Bar */}
      <div className="rounded-xl border border-cream-warm bg-white p-4" data-testid="packet-completeness">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-sm font-bold text-[#333]">Deal Packet Completeness</h3>
            {data.deal_number && <span className="text-[10px] font-mono text-emerald-600">{data.deal_number}</span>}
          </div>
          <span className={`text-2xl font-bold ${
            completeness >= 80 ? "text-emerald-600" : completeness >= 50 ? "text-amber-500" : "text-red-400"
          }`}>{completeness}%</span>
        </div>
        <div className="w-full h-2.5 rounded-full bg-cream overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${completeness}%` }}
            transition={{ duration: 0.6 }}
            className={`h-full rounded-full ${
              completeness >= 80 ? "bg-emerald-500" : completeness >= 50 ? "bg-amber-400" : "bg-red-400"
            }`}
          />
        </div>
        <p className="text-[10px] text-[#999] mt-1.5">
          {completeness < 50 ? "Some sections need borrower input to build a compelling deal packet." :
           completeness < 80 ? "Looking good! A few more sections will make this packet stand out." :
           "This deal packet is ready to send to lending partners."}
        </p>
      </div>

      {/* Section Cards */}
      <div className="space-y-2">
        {SECTIONS
          .filter(section => {
            // Risk Analysis: admin-only + selected lender only
            if (section.key === "risk_analysis") {
              if (isInternal) return true;
              if (userRole === "lender") return false; // Lender sees it only after selection — handled by deal360 context
              return false; // Hide from borrower
            }
            return true;
          })
          .map(section => {
          const status = data.section_status?.[section.key] || "empty";
          const Icon = section.icon;
          const isExpanded = expandedSection === section.key;
          const isEditing = editingSection === section.key;
          // Borrowers can edit certain sections; admins can edit all non-basics/non-photos
          const BORROWER_EDITABLE = ["property", "borrower_profile", "rehab_plan", "exit_strategy", "comps"];
          const canEdit = section.key !== "basics" && section.key !== "photos" && (
            isInternal || (userRole === "borrower" && BORROWER_EDITABLE.includes(section.key))
          );

          return (
            <div key={section.key} className="rounded-xl border border-cream-warm bg-white overflow-hidden" data-testid={`section-${section.key}`}>
              {/* Section Header */}
              <button
                onClick={() => setExpandedSection(isExpanded ? null : section.key)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-cream/30 transition-colors text-left"
                data-testid={`section-toggle-${section.key}`}
              >
                {STATUS_ICONS[status]}
                <Icon className="h-4 w-4 text-[#666]" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#333]">{section.label}</div>
                  <div className="text-[10px] text-[#999]">{section.description}</div>
                </div>
                <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${
                  status === "complete" ? "bg-emerald-100 text-emerald-600" :
                  status === "partial" ? "bg-amber-100 text-amber-600" :
                  "bg-slate-100 text-[#999]"
                }`}>{status}</span>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-[#888]" /> : <ChevronDown className="h-4 w-4 text-[#888]" />}
              </button>

              {/* Section Content */}
              {isExpanded && (
                <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  className="border-t border-cream-warm">
                  <div className="p-4">
                    {/* Edit toggle for users who can edit */}
                    {canEdit && (
                      <div className="flex justify-end mb-3">
                        <button
                          onClick={() => setEditingSection(isEditing ? null : section.key)}
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors ${
                            isEditing ? "bg-gray-100 text-gray-600" : "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
                          }`}
                          data-testid={`edit-toggle-${section.key}`}
                        >
                          {isEditing ? <><RotateCcw className="h-3 w-3" /> Cancel</> : <><Pencil className="h-3 w-3" /> Edit</>}
                        </button>
                      </div>
                    )}

                    {/* Show read-only view by default, edit form when editing */}
                    {!isEditing ? (
                      <SectionReadOnly sectionKey={section.key} form={form} isInternal={isInternal} />
                    ) : (
                      <SectionForm
                        sectionKey={section.key}
                        form={form}
                        setForm={setForm}
                        isInternal={isInternal}
                        onSave={saveSection}
                        saving={saving}
                      />
                    )}
                  </div>
                </motion.div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* Read-only display for each section */
function SectionReadOnly({ sectionKey, form, isInternal }) {
  const DataRow = ({ label, value, source }) => {
    if (!value && value !== 0) return null;
    return (
      <div className="flex justify-between items-center border-b border-cream-warm/60 py-1.5">
        <span className="text-[#888] text-xs">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-[#333] text-xs font-medium text-right">{value}</span>
          {source && <span className="text-[8px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-semibold">{source}</span>}
        </div>
      </div>
    );
  };

  if (sectionKey === "basics") {
    return (
      <div className="space-y-0.5">
        {isInternal && (
          <div className="text-[10px] text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5 mb-2 font-medium">
            Auto-populated from borrower's application
          </div>
        )}
        <DataRow label="Name" value={form.full_name} />
        <DataRow label="Email" value={form.email} />
        <DataRow label="Phone" value={form.phone} />
        <DataRow label="Loan Type" value={form.loan_type?.replace("_", " ")?.toUpperCase()} />
        <DataRow label="Property" value={form.property_address} />
        <DataRow label="State" value={form.state} />
        <DataRow label="Purchase Price" value={form.purchase_price ? fmtUSD(form.purchase_price) : null} />
        <DataRow label="Loan Amount" value={form.requested_loan_amount ? fmtUSD(form.requested_loan_amount) : null} />
        <DataRow label="ARV" value={form.arv ? fmtUSD(form.arv) : null} />
      </div>
    );
  }

  if (sectionKey === "property") {
    const hasData = form.property_type || form.year_built || form.bedrooms || form.bathrooms || form.sqft;
    if (!hasData) return <EmptyState msg="No property details submitted yet." />;
    return (
      <div className="space-y-0.5">
        {isInternal && (
          <div className="text-[10px] text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5 mb-2 font-medium">
            From borrower's application
          </div>
        )}
        <DataRow label="Property Type" value={form.property_type} />
        <DataRow label="Year Built" value={form.year_built} />
        <DataRow label="Bedrooms" value={form.bedrooms} />
        <DataRow label="Bathrooms" value={form.bathrooms} />
        <DataRow label="Square Feet" value={form.sqft?.toLocaleString()} />
        <DataRow label="Lot Size" value={form.lot_size_sqft ? `${form.lot_size_sqft.toLocaleString()} sqft` : null} />
        {form.neighborhood_summary && <DataRow label="Neighborhood" value={form.neighborhood_summary} />}
      </div>
    );
  }

  if (sectionKey === "borrower_profile") {
    const hasData = form.entity_name || form.fico_estimate || form.experience_projects_completed || form.guarantor_name || form.liquidity;
    if (!hasData) return <EmptyState msg="Borrower profile details not yet available." />;
    return (
      <div className="space-y-0.5">
        {isInternal && (
          <div className="text-[10px] text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5 mb-2 font-medium">
            From borrower's application
          </div>
        )}
        <DataRow label="Entity" value={form.entity_name} />
        <DataRow label="FICO / Credit" value={form.fico_estimate} />
        <DataRow label="Experience" value={form.experience_projects_completed ? `${form.experience_projects_completed} projects` : null} />
        <DataRow label="Guarantor" value={form.guarantor_name} />
        <DataRow label="Liquidity" value={form.liquidity ? fmtUSD(form.liquidity) : null} />
        {form.active_rentals && <DataRow label="Active Rentals" value={form.active_rentals} />}
      </div>
    );
  }

  if (sectionKey === "rehab_plan") {
    const items = form.rehab_items || [];
    const budget = form.rehab_budget;
    if (items.length === 0 && !budget && !form.rehab_timeline_months) return <EmptyState msg="No rehab plan submitted." />;
    return (
      <div className="space-y-2">
        {isInternal && (
          <div className="text-[10px] text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5 mb-1 font-medium">
            From borrower's application
          </div>
        )}
        {budget > 0 && <DataRow label="Rehab Budget" value={fmtUSD(budget)} />}
        {form.rehab_timeline_months && <DataRow label="Timeline" value={`${form.rehab_timeline_months} months`} />}
        {form.rehab_contingency > 0 && <DataRow label="Contingency" value={fmtUSD(form.rehab_contingency)} />}
        {items.length > 0 && (
          <div className="mt-2 rounded-lg border border-cream-warm overflow-hidden">
            <table className="w-full text-xs">
              <thead><tr className="bg-cream"><th className="text-left px-3 py-1.5 text-[#888]">Item</th><th className="text-right px-3 py-1.5 text-[#888]">Cost</th></tr></thead>
              <tbody className="divide-y divide-cream-warm">
                {items.map((item, i) => (
                  <tr key={i}><td className="px-3 py-1.5 text-[#333]">{item.name}</td><td className="px-3 py-1.5 text-right font-medium text-[#333]">{fmtUSD(item.cost)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  if (sectionKey === "exit_strategy") {
    const hasData = form.exit_strategy || form.exit_strategy_primary || form.projected_sale_price || form.closing_timeline_days;
    if (!hasData) return <EmptyState msg="Exit strategy not yet defined." />;
    return (
      <div className="space-y-0.5">
        {isInternal && (
          <div className="text-[10px] text-blue-600 bg-blue-50 rounded-lg px-3 py-1.5 mb-2 font-medium">
            From borrower's application
          </div>
        )}
        <DataRow label="Strategy" value={form.exit_strategy_primary || form.exit_strategy} />
        <DataRow label="Closing Timeline" value={form.closing_timeline_days ? `${form.closing_timeline_days} days` : null} />
        <DataRow label="Projected Sale Price" value={form.projected_sale_price ? fmtUSD(form.projected_sale_price) : null} />
        <DataRow label="Rent Estimate" value={form.rent_estimate_monthly ? `${fmtUSD(form.rent_estimate_monthly)}/mo` : null} />
        {form.selling_costs && <DataRow label="Selling Costs" value={fmtUSD(form.selling_costs)} />}
        {form.exit_strategy_secondary && <DataRow label="Secondary" value={form.exit_strategy_secondary} />}
      </div>
    );
  }

  if (sectionKey === "comps") {
    const comps = form.comps || [];
    if (comps.length === 0) return <EmptyState msg="No comparable sales added yet." />;
    return (
      <div className="rounded-lg border border-cream-warm overflow-hidden">
        <table className="w-full text-xs">
          <thead><tr className="bg-cream">
            <th className="text-left px-3 py-1.5 text-[#888]">Address</th>
            <th className="text-right px-3 py-1.5 text-[#888]">Price</th>
            <th className="text-right px-3 py-1.5 text-[#888]">Sqft</th>
            <th className="text-right px-3 py-1.5 text-[#888]">Date</th>
          </tr></thead>
          <tbody className="divide-y divide-cream-warm">
            {comps.map((c, i) => (
              <tr key={i}>
                <td className="px-3 py-1.5 text-[#333]">{c.address}</td>
                <td className="px-3 py-1.5 text-right font-medium">{c.sold_price ? fmtUSD(c.sold_price) : "-"}</td>
                <td className="px-3 py-1.5 text-right">{c.sqft || "-"}</td>
                <td className="px-3 py-1.5 text-right text-[#888]">{c.date || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (sectionKey === "photos") {
    return (
      <div className="text-xs text-[#888] space-y-2">
        <p>Property photos are managed separately in the <strong>Photos</strong> tab.</p>
        <p className="font-medium text-[#555]">Recommended: Exterior, Living Room, Kitchen, Bathrooms, Backyard</p>
        <p className="text-[10px] text-blue-600">Photos are automatically included in the deal deck when generated.</p>
      </div>
    );
  }

  if (sectionKey === "risk_analysis") {
    const strengths = form.strengths || [];
    const risks = form.risks || [];
    const aiSummary = form.ai_summary;
    if (strengths.length === 0 && risks.length === 0 && !aiSummary) {
      return <EmptyState msg="AI risk analysis will be generated when the outreach pipeline runs." />;
    }
    return (
      <div className="space-y-3">
        <div className="text-[10px] text-purple-600 bg-purple-50 rounded-lg px-3 py-1.5 font-medium flex items-center gap-1.5">
          <Shield className="h-3 w-3" /> AI-generated from deal underwriting analysis
        </div>
        {aiSummary && (
          <div className="rounded-lg border border-cream-warm bg-cream p-3 text-xs text-[#333] leading-relaxed">{aiSummary}</div>
        )}
        {strengths.length > 0 && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
            <h5 className="text-[10px] uppercase tracking-wider text-emerald-600 mb-1.5 font-semibold">Strengths</h5>
            {strengths.map((s, i) => (
              <div key={i} className="text-xs text-[#333] flex gap-1.5 mb-1"><CheckCircle className="h-3.5 w-3.5 text-emerald-500 shrink-0 mt-0.5" />{s}</div>
            ))}
          </div>
        )}
        {risks.length > 0 && (
          <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
            <h5 className="text-[10px] uppercase tracking-wider text-orange-600 mb-1.5 font-semibold">Risks</h5>
            {risks.map((r, i) => (
              <div key={i} className="text-xs text-[#333] flex gap-1.5 mb-1"><Circle className="h-3.5 w-3.5 text-orange-400 shrink-0 mt-0.5" />{r}</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return null;
}

function EmptyState({ msg }) {
  return <p className="text-xs text-[#999] py-2">{msg}</p>;
}

/* Stable Input component — outside SectionForm to prevent focus loss on re-render */
function PacketInput({ label, field, type = "text", placeholder = "", value, onChange }) {
  return (
    <div>
      <label className="text-[10px] font-semibold text-[#666] uppercase tracking-wider block mb-1">{label}</label>
      <input type={type} value={value || ""} onChange={e => onChange(field, type === "number" ? (e.target.value ? Number(e.target.value) : "") : e.target.value)}
        placeholder={placeholder} data-testid={`input-${field}`}
        className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400 outline-none bg-white" />
    </div>
  );
}

function PacketSaveButton({ sectionKey, fields, onSave, saving }) {
  return (
    <button onClick={() => onSave(fields)} disabled={saving} data-testid={`save-${sectionKey}`}
      className="mt-3 px-4 py-2 rounded-lg bg-emerald-700 text-white text-xs font-semibold hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-1.5">
      {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />} Save
    </button>
  );
}

/* Editable form for each section */
function SectionForm({ sectionKey, form, setForm, isInternal, onSave, saving }) {
  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const SaveButton = ({ fields }) => (
    <PacketSaveButton sectionKey={sectionKey} fields={fields} onSave={onSave} saving={saving} />
  );

  if (sectionKey === "property") {
    return (<>
      <div className="grid grid-cols-3 gap-3">
        <PacketInput label="Property Type" field="property_type" placeholder="single_family" value={form.property_type} onChange={update} />
        <PacketInput label="Year Built" field="year_built" type="number" placeholder="1985" value={form.year_built} onChange={update} />
        <PacketInput label="Bedrooms" field="bedrooms" type="number" placeholder="3" value={form.bedrooms} onChange={update} />
        <PacketInput label="Bathrooms" field="bathrooms" type="number" placeholder="2" value={form.bathrooms} onChange={update} />
        <PacketInput label="Square Footage" field="sqft" type="number" placeholder="1850" value={form.sqft} onChange={update} />
        <PacketInput label="Lot Size (sqft)" field="lot_size_sqft" type="number" placeholder="7200" value={form.lot_size_sqft} onChange={update} />
      </div>
      <div className="mt-3">
        <label className="text-[10px] font-semibold text-[#666] uppercase tracking-wider block mb-1">Neighborhood Summary</label>
        <textarea value={form.neighborhood_summary || ""} onChange={e => update("neighborhood_summary", e.target.value)}
          placeholder="Strong resale market, high demand for renovated homes..."
          data-testid="input-neighborhood_summary" rows={2}
          className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none resize-none bg-white" />
      </div>
      <SaveButton fields={["property_type", "year_built", "bedrooms", "bathrooms", "sqft", "lot_size_sqft", "neighborhood_summary"]} />
    </>);
  }

  if (sectionKey === "borrower_profile") {
    return (<>
      <div className="grid grid-cols-2 gap-3">
        <PacketInput label="Entity Name" field="entity_name" placeholder="Williams Property Group LLC" value={form.entity_name} onChange={update} />
        <PacketInput label="FICO / Credit Score" field="fico_estimate" placeholder="740" value={form.fico_estimate} onChange={update} />
        <PacketInput label="Projects Completed" field="experience_projects_completed" type="number" placeholder="12" value={form.experience_projects_completed} onChange={update} />
        <PacketInput label="Guarantor Name" field="guarantor_name" placeholder="John Smith" value={form.guarantor_name} onChange={update} />
        <PacketInput label="Liquidity ($)" field="liquidity" type="number" placeholder="180000" value={form.liquidity} onChange={update} />
        <PacketInput label="Active Rental Properties" field="active_rentals" type="number" placeholder="4" value={form.active_rentals} onChange={update} />
        <PacketInput label="Avg Flip Timeline (months)" field="avg_flip_timeline_months" type="number" placeholder="5" value={form.avg_flip_timeline_months} onChange={update} />
        <PacketInput label="Avg Profit Per Flip ($)" field="avg_profit_per_flip" type="number" placeholder="85000" value={form.avg_profit_per_flip} onChange={update} />
      </div>
      <SaveButton fields={["entity_name", "fico_estimate", "experience_projects_completed", "guarantor_name", "liquidity", "active_rentals", "avg_flip_timeline_months", "avg_profit_per_flip"]} />
    </>);
  }

  if (sectionKey === "rehab_plan") {
    const items = form.rehab_items || [];
    const addItem = () => update("rehab_items", [...items, { name: "", cost: 0 }]);
    const removeItem = (idx) => update("rehab_items", items.filter((_, i) => i !== idx));
    const updateItem = (idx, field, val) => {
      const newItems = [...items];
      newItems[idx] = { ...newItems[idx], [field]: field === "cost" ? Number(val) : val };
      update("rehab_items", newItems);
    };
    return (<>
      <div className="grid grid-cols-2 gap-3 mb-3">
        <PacketInput label="Rehab Budget ($)" field="rehab_budget" type="number" placeholder="75000" value={form.rehab_budget} onChange={update} />
        <PacketInput label="Timeline (months)" field="rehab_timeline_months" type="number" placeholder="5" value={form.rehab_timeline_months} onChange={update} />
        <PacketInput label="Contingency ($)" field="rehab_contingency" type="number" placeholder="20000" value={form.rehab_contingency} onChange={update} />
      </div>
      <label className="text-[10px] font-semibold text-[#666] uppercase tracking-wider block mb-1">Itemized Scope</label>
      <div className="space-y-2">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={item.name} onChange={e => updateItem(i, "name", e.target.value)} placeholder="Kitchen Remodel"
              className="flex-1 px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none" />
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-[#888]">$</span>
              <input type="number" value={item.cost || ""} onChange={e => updateItem(i, "cost", e.target.value)} placeholder="20000"
                className="w-32 pl-6 pr-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none" />
            </div>
            <button onClick={() => removeItem(i)} className="p-1.5 text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <button onClick={addItem} data-testid="add-rehab-item"
          className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium hover:text-emerald-700">
          <Plus className="h-3.5 w-3.5" /> Add Rehab Item
        </button>
      </div>
      <SaveButton fields={["rehab_budget", "rehab_items", "rehab_contingency", "rehab_timeline_months"]} />
    </>);
  }

  if (sectionKey === "exit_strategy") {
    return (<>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] font-semibold text-[#666] uppercase tracking-wider block mb-1">Primary Exit Strategy</label>
          <select value={form.exit_strategy_primary || form.exit_strategy || ""} onChange={e => update("exit_strategy_primary", e.target.value)}
            data-testid="input-exit_strategy_primary"
            className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm bg-white focus:border-emerald-400 outline-none">
            <option value="">Select...</option>
            <option value="sale_after_renovation">Sale After Renovation</option>
            <option value="refinance_to_rental">Refinance to Rental Loan</option>
            <option value="hold_as_rental">Hold as Rental</option>
            <option value="Hold as rental">Hold as Rental</option>
            <option value="sell_as_is">Sell As-Is</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <PacketInput label="Projected Sale Price ($)" field="projected_sale_price" type="number" placeholder="475000" value={form.projected_sale_price} onChange={update} />
          <PacketInput label="Selling Costs ($)" field="selling_costs" type="number" placeholder="35000" value={form.selling_costs} onChange={update} />
          <PacketInput label="Closing Timeline (days)" field="closing_timeline_days" type="number" placeholder="30" value={form.closing_timeline_days} onChange={update} />
          <PacketInput label="Rent Estimate ($/month)" field="rent_estimate_monthly" type="number" placeholder="3200" value={form.rent_estimate_monthly} onChange={update} />
        </div>
        <div>
          <label className="text-[10px] font-semibold text-[#666] uppercase tracking-wider block mb-1">Secondary Exit Strategy</label>
          <textarea value={form.exit_strategy_secondary || ""} onChange={e => update("exit_strategy_secondary", e.target.value)}
            placeholder="Refinance into DSCR rental loan if market conditions shift..."
            data-testid="input-exit_strategy_secondary" rows={2}
            className="w-full px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none resize-none bg-white" />
        </div>
      </div>
      <SaveButton fields={["exit_strategy_primary", "exit_strategy_secondary", "projected_sale_price", "selling_costs", "closing_timeline_days", "rent_estimate_monthly"]} />
    </>);
  }

  if (sectionKey === "comps") {
    const comps = form.comps || [];
    const addComp = () => update("comps", [...comps, { address: "", sold_price: 0, sqft: 0, date: "" }]);
    const removeComp = (idx) => update("comps", comps.filter((_, i) => i !== idx));
    const updateComp = (idx, field, val) => {
      const newComps = [...comps];
      newComps[idx] = { ...newComps[idx], [field]: ["sold_price", "sqft"].includes(field) ? Number(val) : val };
      update("comps", newComps);
    };
    return (<>
      <div className="space-y-2">
        {comps.map((comp, i) => (
          <div key={i} className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-center">
            <input value={comp.address} onChange={e => updateComp(i, "address", e.target.value)} placeholder="123 Oak St"
              className="col-span-2 px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none" />
            <input type="number" value={comp.sold_price || ""} onChange={e => updateComp(i, "sold_price", e.target.value)} placeholder="$475,000"
              className="px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none" />
            <input value={comp.date} onChange={e => updateComp(i, "date", e.target.value)} placeholder="Feb 2026"
              className="px-3 py-2 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none" />
            <button onClick={() => removeComp(i)} className="p-1.5 text-red-400 hover:text-red-600 justify-self-center"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        <button onClick={addComp} data-testid="add-comp"
          className="flex items-center gap-1.5 text-xs text-emerald-600 font-medium hover:text-emerald-700">
          <Plus className="h-3.5 w-3.5" /> Add Comparable Sale
        </button>
      </div>
      <SaveButton fields={["comps"]} />
    </>);
  }

  if (sectionKey === "risk_analysis") {
    const ListEditor = ({ label, field }) => {
      const items = form[field] || [];
      const addItem = () => update(field, [...items, ""]);
      const removeItem = (idx) => update(field, items.filter((_, i) => i !== idx));
      const updateItem = (idx, val) => { const n = [...items]; n[idx] = val; update(field, n); };
      return (
        <div className="mb-3">
          <label className="text-[10px] font-semibold text-[#666] uppercase tracking-wider block mb-1">{label}</label>
          {items.map((item, i) => (
            <div key={i} className="flex items-center gap-2 mb-1">
              <input value={item} onChange={e => updateItem(i, e.target.value)} placeholder={`Enter ${label.toLowerCase()}...`}
                className="flex-1 px-3 py-1.5 rounded-lg border border-cream-warm text-sm focus:border-emerald-400 outline-none" />
              <button onClick={() => removeItem(i)} className="p-1 text-red-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
            </div>
          ))}
          <button onClick={addItem} className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium hover:text-emerald-700">
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
      );
    };
    return (<>
      <ListEditor label="Strengths" field="strengths" />
      <ListEditor label="Risks" field="risks" />
      <ListEditor label="Risk Mitigants" field="risk_mitigants" />
      <SaveButton fields={["strengths", "risks", "risk_mitigants"]} />
    </>);
  }

  return null;
}
